import Image from 'next/image'
import type { ComponentType, ReactNode } from 'react'
import { AlertCircle } from 'lucide-react'

/**
 * Shared chrome for the signed-out surface (sign-in, forgot/reset password,
 * accept-invite), per Figma "Sign-In & Password Reset". Flat canvas background;
 * the 440px card appears at sm+ while mobile lays content directly on the page.
 */
export function AuthShell({ children }: { children: ReactNode }) {
    return (
        <main className="flex min-h-screen flex-col items-center justify-center bg-[var(--bg-canvas)] px-6 py-10">
            <div className="w-full max-w-110 sm:rounded-lg sm:border sm:border-[var(--border-subtle)] sm:bg-[var(--bg-surface)] sm:p-10 sm:shadow-[var(--shadow-auth-card)]">
                {children}
            </div>
        </main>
    )
}

export function AuthHeader({ title, subtitle }: { title: string; subtitle: ReactNode }) {
    return (
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
            <Image
                src="/kehilla-logo.png"
                alt="KehillaHQ"
                width={40}
                height={44}
                className="h-11 w-auto object-contain"
                priority
            />
            <h1 className="font-serif text-3xl font-medium leading-tight text-[var(--text-primary)]">
                {title}
            </h1>
            <p className="text-base leading-6 text-[var(--text-secondary)]">{subtitle}</p>
        </div>
    )
}

/**
 * Icon-led status screen ("Check your email", "This link has expired",
 * "You're all set"). Actions stack full-width below the copy.
 */
export function AuthStatus({
    icon: Icon,
    title,
    children,
    actions,
}: {
    icon: ComponentType<{ className?: string }>
    title: string
    children: ReactNode
    actions?: ReactNode
}) {
    return (
        <div className="flex flex-col items-center text-center">
            <div className="mb-3 flex size-18 items-center justify-center rounded-full bg-[var(--bg-brand-subtle)]">
                <Icon className="size-8 text-[var(--text-primary)]" />
            </div>
            <h1 className="font-serif text-3xl font-medium leading-tight text-[var(--text-primary)]">
                {title}
            </h1>
            <div className="mt-3 text-base leading-6 text-[var(--text-secondary)]">{children}</div>
            {actions && <div className="mt-6 flex w-full flex-col gap-3">{actions}</div>}
        </div>
    )
}

export function AuthErrorBanner({ children }: { children: ReactNode }) {
    return (
        <div
            role="alert"
            className="flex items-start gap-2.5 rounded-lg border border-[var(--border-danger)] bg-[var(--bg-danger-subtle)] px-3.5 py-3"
        >
            <AlertCircle aria-hidden className="mt-px size-5 shrink-0 text-[var(--text-danger)]" />
            <p className="text-sm leading-5 text-[var(--text-danger)]">{children}</p>
        </div>
    )
}

/** Inline per-field validation message; link it to the input via aria-describedby. */
export function FieldError({ id, children }: { id?: string; children: ReactNode }) {
    return (
        <p id={id} className="flex items-start gap-1.5 text-sm leading-5 text-[var(--text-danger)]">
            <AlertCircle aria-hidden className="mt-0.5 size-4 shrink-0" />
            <span>{children}</span>
        </p>
    )
}
